// The single source of truth for which backend a given build may talk to.
//
// Build and target are separate facts: which bundle you are running, and which
// Firebase project it points at. Most mismatches between them are silent — a
// production bundle wired to staging looks entirely normal and writes to the
// wrong database — so they have to be caught by assertion rather than by
// noticing.
//
// vite.config.mjs checks this at build time, which is the only place a bad
// artifact can be prevented from existing at all. InDevNotification reads the
// same tables, so the banner and the build check cannot drift apart about what
// "production" means.

// Every legitimate deployment, written as the exact triple it must appear as.
// The region matters as much as the project: cloud functions are deployed per
// region, so a bundle carrying the wrong one calls an address where nothing is
// listening, and every callable fails at runtime having built cleanly.
export const Deployments = [
  {
    environment: 'LOCAL',
    projectId: 'fixit-staging',
    region: 'us-central1',
    target: 'Emulator',
    // Never a build. A LOCAL bundle calls connectAuthEmulator and friends
    // against localhost, which on a deployed site is a machine that does not
    // exist — the app loads and then fails at the first authenticated call.
    buildable: false
  },
  {
    environment: 'STAGING',
    projectId: 'fixit-staging',
    region: 'europe-west1',
    target: 'Staging',
    buildable: true
  },
  {
    environment: 'PRODUCTION',
    projectId: 'fixit-apps',
    region: 'europe-west1',
    target: 'Production',
    buildable: true
  },
  {
    environment: 'PRODUCTION',
    projectId: 'fixit-uk',
    region: 'europe-west2',
    target: 'Production',
    buildable: true
  }
]

export const findDeployment = (environment, projectId) =>
  Deployments.find(
    deployment =>
      deployment.environment === environment && deployment.projectId === projectId
  ) || null

// What a project id means as a backend, for display.
//
// Only deployable entries are considered: fixit-staging appears twice, once as
// the emulator's project and once as the real staging backend, and "Emulator"
// describes the build rather than the project. A LOCAL build labels its own
// target without asking here.
//
// Unrecognised project ids fall through to the raw id rather than being guessed
// at, so a misconfigured build is visible rather than mislabelled.
export const targetLabel = projectId => {
  const deployment = Deployments.find(
    entry => entry.buildable && entry.projectId === projectId
  )
  return (deployment && deployment.target) || projectId || 'Unknown'
}

// Used to decide whether the environment banner is suppressed. Deliberately
// stricter than `environment === "PRODUCTION"`: a production bundle pointed at
// staging is not a production deployment, and is exactly the case that must
// stay visible.
export const isProductionDeployment = (environment, projectId) => {
  const deployment = findDeployment(environment, projectId)
  return Boolean(deployment) && deployment.target === 'Production'
}

// Returns a human-readable reason the combination is not deployable, or null if
// it is fine. A string rather than a thrown error so that the same check can be
// used somewhere that wants to report rather than abort.
export const environmentError = ({ environment, projectId, region, isBuild = false }) => {
  if (!environment) return 'VITE_ENVIRONMENT is not set.'
  if (!projectId) return 'VITE_FIREBASE_PROJECT_ID is not set.'
  if (!region) return 'VITE_CLOUD_FUNCTION_REGION is not set.'

  const deployment = findDeployment(environment, projectId)
  if (!deployment) {
    return (
      `A ${environment} build targeting "${projectId}" is not a known deployment. ` +
      `Expected one of: ${Deployments.map(
        entry => `${entry.environment} -> ${entry.projectId}`
      ).join(', ')}.`
    )
  }

  if (isBuild && !deployment.buildable) {
    return (
      `A ${environment} bundle must not be built: it connects to emulators on ` +
      'localhost, which will not exist wherever it is deployed.'
    )
  }

  if (deployment.region !== region) {
    return (
      `A ${environment} build targeting "${projectId}" must use region ` +
      `"${deployment.region}", not "${region}". Cloud functions are deployed ` +
      'per region, so every callable would fail at runtime.'
    )
  }

  return null
}
